import { useCallback } from "react";
import { ethers } from "ethers";
import type { BaseStoredNote, BaseStatusMessage } from "../types";
import type { JoinSplitNote } from "../../joinsplit/types";
import type { OdosQuoteResponse } from "./useBaseSwapQuote";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { executeFlatJoinSplitV2Swap } from "../../joinsplit-v2/operations";
import {
  buildPrivateSwapRoute,
  quotePrivateSwap,
  swapInputAmount,
  muchfiInputAmount,
} from "../../joinsplit-v2/uniswap";
import { getBaseReadProvider } from "../../utils/base-read-provider";
import { friendlyError } from "../utils/errors";
import { executePrivateFeeSwap } from "../../joinsplit-v2/privateSwap";
import { privateSwapDeployment } from "../../joinsplit-v2/privateSwapConfig";
import { MUCHFI_CHIKYU } from "../../config/muchfi";
import { fetchMuchfiFee } from "../../joinsplit-v2/muchfiFee";

export interface SwapOperationResult {
  success: boolean;
  /** V2 outputs are delivered through the encrypted settlement journal. */
  journalId?: string;
  outputNotes?: JoinSplitNote[];
  outputNote?: JoinSplitNote;
  changeNote?: JoinSplitNote;
  spentCommitments?: string[];
  depositTxHash?: string;
  outputAmountStr?: string;
  /** True when the swap reverted but input tokens were re-deposited into the shielded pools. */
  recovered?: boolean;
}

interface UseBaseSwapOperationParams {
  signer: ethers.Signer | null;
  spendingKeys: any;
  notes: BaseStoredNote[];
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
  setStatus: (status: BaseStatusMessage | null) => void;
  setLoading: (loading: boolean) => void;
}

export function useBaseSwapOperation({
  signer,
  spendingKeys,
  notes,
  addLog,
  setStatus,
  setLoading,
}: UseBaseSwapOperationParams) {
  const handleSwap = useCallback(
    async (
      inputToken: TokenInfo,
      outputToken: TokenInfo,
      amountStr: string,
      slippageBps: string,
      quote: OdosQuoteResponse | null,
    ): Promise<SwapOperationResult> => {
      const fail = { success: false } as SwapOperationResult;
      if (!signer || !spendingKeys) {
        setStatus({ type: "error", message: "Connect wallet first" });
        return fail;
      }
      setLoading(true);
      setStatus({ type: "info", message: "Preparing transaction…" });
      try {
        if (
          !EVM_CONFIG.PRIVATE_SWAP_ENABLED ||
          !EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS
        )
          throw new Error(
            "Relayed private swaps are not enabled on this deployment",
          );
        const amount = ethers.parseUnits(amountStr, inputToken.decimals);
        if (privateSwapDeployment()) {
          if (
            !quote?.privateFeesSwap ||
            quote.privateFeesSwap.outputToken !==
              ethers.getAddress(outputToken.address)
          )
            throw Error("Wait for a matching swap quote");
          const result = await executePrivateFeeSwap({
            signer,
            spendingKeys,
            notes,
            tokenAddress: inputToken.address,
            tokenDecimals: inputToken.decimals,
            amount,
            recipient: ethers.ZeroAddress,
            fee: 0n,
            relay: true,
            swapQuote: quote.privateFeesSwap,
            slippageBps: Number(slippageBps),
            onLog: (message, type, hash) => {
              addLog(message, type, hash);
              if (/Submitting through the relayer/.test(message))
                setStatus({ type: "info", message: "Submitting transaction…" });
              else if (/Settlement submitted/.test(message))
                setStatus({
                  type: "info",
                  message: "Waiting for confirmation…",
                });
            },
          });
          if (!result.success) {
            setStatus({ type: "error", message: friendlyError(result.error) });
            return {
              success: false,
              journalId: result.journalId,
              depositTxHash: result.txHash,
            };
          }
          setStatus({ type: "success", message: "Swap complete" });
          return {
            success: true,
            depositTxHash: result.txHash,
            journalId: result.journalId,
            spentCommitments: result.spentNotes.map((n) => n.commitment),
            outputNotes: result.outputNotes,
          };
        }
        const prior = quote?.privateSwap;
        const pool = EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT;
        const muchfi = EVM_CONFIG.CHAIN_ID === MUCHFI_CHIKYU.chainId;
        const { relayFee, inputAmount } = muchfi
          ? muchfiInputAmount(
              amount,
              prior ? BigInt(prior.relayFee) : undefined,
            )
          : swapInputAmount(amount, EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS);
        if (
          !prior ||
          prior.chainId !== EVM_CONFIG.CHAIN_ID ||
          prior.pool !== ethers.getAddress(pool) ||
          prior.inputToken !== ethers.getAddress(inputToken.address) ||
          prior.outputToken !== ethers.getAddress(outputToken.address) ||
          prior.amount !== amount.toString() ||
          prior.inputAmount !== inputAmount.toString() ||
          prior.relayFee !== relayFee.toString()
        )
          throw new Error("Wait for a quote matching your swap amount");
        if (muchfi) {
          const currentFee = await fetchMuchfiFee(
            notes,
            inputToken.address,
            outputToken.address,
            amount,
          );
          if (relayFee < currentFee.minimumFee)
            throw Error(
              "Network fees increased beyond your approved quote. Refresh the quote before continuing.",
            );
        }
        const deadline = BigInt(Math.floor(Date.now() / 1000) + 600);
        const oldRoute = buildPrivateSwapRoute(
          prior,
          Number(slippageBps),
          deadline,
        );
        const fresh = await quotePrivateSwap({
          provider: getBaseReadProvider(),
          chainId: EVM_CONFIG.CHAIN_ID,
          pool,
          inputToken: inputToken.address,
          outputToken: outputToken.address,
          amount,
          relayFeeBps: EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS,
          ...(muchfi ? { relayFee } : {}),
        });
        if (
          fresh.adapter !== prior.adapter ||
          fresh.protocolFeeBps !== prior.protocolFeeBps
        )
          throw new Error(
            "Swap configuration changed. Refresh the quote before continuing",
          );
        const freshNet =
          BigInt(fresh.grossOutput) -
          (BigInt(fresh.grossOutput) * BigInt(fresh.protocolFeeBps)) / 10_000n;
        if (freshNet < oldRoute.minOutputAmount)
          throw new Error(
            "The price moved beyond your slippage tolerance. Refresh the quote",
          );
        // Keep the displayed quote's floor; only the best available fee tier may change.
        const route = buildPrivateSwapRoute(
          { ...prior, feeTier: fresh.feeTier },
          Number(slippageBps),
          deadline,
        );
        addLog(
          `Preparing private swap: ${amountStr} ${inputToken.symbol} → ${outputToken.symbol}`,
          "info",
        );
        setStatus({ type: "info", message: "Preparing transaction…" });
        const result = await executeFlatJoinSplitV2Swap({
          signer,
          spendingKeys,
          notes,
          tokenAddress: inputToken.address,
          amount,
          outputTokenAddress: outputToken.address,
          ...route,
          fee: relayFee,
          relay: true,
          onLog: (message, type, hash) => {
            addLog(message, type, hash);
            if (/proof|proving/i.test(message))
              setStatus({ type: "info", message: "Creating private proof…" });
            else if (/Submitting/i.test(message))
              setStatus({ type: "info", message: "Submitting transaction…" });
            else if (/submitted|finality/i.test(message))
              setStatus({ type: "info", message: "Waiting for confirmation…" });
          },
        });
        if (!result.success) {
          if (result.journalId) {
            const message =
              result.error ||
              "Swap submission requires journal recovery before retrying.";
            setStatus({ type: "error", message });
            addLog(message, "warning", result.txHash);
            return {
              success: false,
              journalId: result.journalId,
              depositTxHash: result.txHash,
            };
          }
          throw new Error(result.error || "Private swap failed");
        }
        setStatus({
          type: "success",
          message: "Swap complete",
        });
        return {
          success: true,
          depositTxHash: result.txHash,
          journalId: result.journalId,
          spentCommitments: result.spentNotes.map((note) => note.commitment),
          outputNotes: result.outputNotes,
        };
      } catch (error) {
        const message = friendlyError(error);
        setStatus({ type: "error", message });
        addLog(`Swap failed: ${message}`, "error");
        return fail;
      } finally {
        setLoading(false);
      }
    },
    [signer, spendingKeys, notes, addLog, setStatus, setLoading],
  );

  return { handleSwap };
}
